import logger from '@/utils/logger';
/**
 * COMPOSANTS: SECTIONS PARAMÈTRES
 * Toutes les sections de la page Settings
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Download, Calendar, Filter, TrendingUp, TrendingDown, Zap, AlertCircle } from 'lucide-react';
import ManageSubscription from './ManageSubscription';

// ============================================
// SECTION: COMPTE & SÉCURITÉ
// ============================================

interface AccountSectionProps {
  user: any;
  onSave: () => void;
  onError: (message: string) => void;
}

export function AccountSection({ user, onSave, onError }: AccountSectionProps) {
  const [changingEmail, setChangingEmail] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChangeEmail = async () => {
    if (!newEmail) {
      onError('Veuillez entrer un nouvel email');
      return;
    }

    setChangingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      
      if (error) throw error;
      
      onSave();
      setNewEmail('');
    } catch (error: any) {
      onError(error.message || 'Erreur lors du changement d\'email');
    } finally {
      setChangingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      onError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      onError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      onError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) throw error;
      
      onSave();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      onError(error.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Adresse email</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Email actuel</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-900 border border-theme-border/50 rounded-xl text-gray-500 dark:text-gray-600 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Nouvel email</label>
            <div className="flex gap-3">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
                placeholder="nouveau@email.com"
              />
              <button
                onClick={handleChangeEmail}
                disabled={changingEmail || !newEmail}
                className="px-6 py-3 bg-accent-primary hover:bg-accent-secondary text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changingEmail ? 'Envoi...' : 'Changer'}
              </button>
            </div>
            <p className="text-xs text-theme-tertiary mt-2">Un email de confirmation sera envoyé à votre nouvelle adresse</p>
          </div>
        </div>
      </div>

      {/* Mot de passe */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Mot de passe</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Nouveau mot de passe</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
              placeholder="Minimum 8 caractères"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
              placeholder="Répétez le mot de passe"
            />
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !newPassword || !confirmPassword}
            className="px-6 py-3 bg-accent-primary hover:bg-accent-secondary text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {changingPassword ? 'Modification...' : 'Changer le mot de passe'}
          </button>
        </div>
      </div>

      {/* 2FA (placeholder) */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Authentification à deux facteurs</h3>
        <p className="text-theme-secondary text-sm mb-4">
          Ajoutez une couche de sécurité supplémentaire à votre compte
        </p>
        <button className="px-6 py-3 bg-theme-hover hover:bg-theme-elevated text-theme-primary font-semibold rounded-xl transition-colors">
          Activer 2FA (Bientôt)
        </button>
      </div>
    </div>
  );
}

// ============================================
// SECTION: ABONNEMENT & TOKENS (avec sous-onglets)
// ============================================

interface SubscriptionSectionProps {
  tokenBalance: any;
  onNavigate: () => void;
}

interface TokenTransaction {
  id: string;
  transaction_type: string;
  tokens_amount: number;
  tokens_balance_after: number;
  description: string;
  created_at: string;
  metadata?: any;
}

export function SubscriptionSection({ tokenBalance, onNavigate }: SubscriptionSectionProps) {
  const formatTokens = (tokens: number | undefined) => {
    if (tokens === undefined || tokens === null) return '0';
    return tokens.toLocaleString('fr-FR');
  };
  const [subTab, setSubTab] = useState<'balance' | 'history' | 'manage'>('balance');
  
  // États pour l'historique
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');
  
  // Charger les transactions
  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      let query = supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (filter !== 'all') {
        query = query.eq('transaction_type', filter);
      }
      
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate = new Date();
        
        switch (dateRange) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
        }
        
        if (dateRange !== 'all') {
          query = query.gte('created_at', startDate.toISOString());
        }
      }
      
      const { data, error } = await query;
      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      logger.error('Erreur chargement transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Charger transactions quand on switch vers l'onglet historique
  useEffect(() => {
    if (subTab === 'history') {
      loadTransactions();
    }
  }, [subTab, filter, dateRange]);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'purchase': return <TrendingUp className="w-5 h-5 text-green-400" />;
      case 'renewal': return <Calendar className="w-5 h-5 text-blue-400" />;
      case 'usage': return <TrendingDown className="w-5 h-5 text-accent-tertiary" />;
      case 'bonus': return <Zap className="w-5 h-5 text-accent-violet" />;
      case 'rollover': return <Calendar className="w-5 h-5 text-accent-secondary" />;
      default: return <Zap className="w-5 h-5 text-theme-secondary" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'purchase': return 'text-green-400';
      case 'renewal': return 'text-blue-400';
      case 'usage': return 'text-red-400';
      case 'bonus': return 'text-purple-400';
      case 'rollover': return 'text-accent-secondary';
      default: return 'text-theme-secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Sous-navigation */}
      <div className="flex gap-3 border-b border-theme-border pb-4">
        <button
          onClick={() => setSubTab('balance')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
            subTab === 'balance'
              ? 'bg-theme-selected text-accent-primary border-theme-selected'
              : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover/50'
          }`}
        >
          Balance & Plan
        </button>
        <button
          onClick={() => setSubTab('history')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
            subTab === 'history'
              ? 'bg-theme-selected text-accent-primary border-theme-selected'
              : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover/50'
          }`}
        >
          Historique Tokens
        </button>
        {tokenBalance.currentPlan && tokenBalance.currentPlan !== 'free' && (
          <button
            onClick={() => setSubTab('manage')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              subTab === 'manage'
                ? 'bg-theme-selected text-accent-primary border-theme-selected'
                : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover/50'
            }`}
          >
            Gérer mon abonnement
          </button>
        )}
      </div>
      
      {/* Contenu selon sous-onglet */}
      {subTab === 'balance' ? (
        <div className="space-y-6">
          {/* Balance actuelle */}
          <div className="p-8 rounded-2xl bg-theme-surface border border-theme-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-theme-primary mb-2">Votre Balance</h3>
                <p className="text-theme-secondary text-sm">Tokens disponibles pour vos analyses</p>
              </div>
              <button
                onClick={onNavigate}
                className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:from-accent-secondary hover:to-accent-tertiary text-white font-semibold rounded-xl transition-all"
              >
                Recharger
              </button>
            </div>

            <div className="text-center py-6">
              <div className="text-6xl font-black text-accent-primary mb-2">
                {formatTokens(tokenBalance.balance)}
              </div>
              <div className="text-theme-secondary">tokens disponibles</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
              <div className="text-sm text-theme-secondary mb-1">Total Acheté</div>
              <div className="text-2xl font-bold text-theme-primary">{formatTokens(tokenBalance.totalPurchased)}</div>
            </div>

            <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
              <div className="text-sm text-theme-secondary mb-1">Total Utilisé</div>
              <div className="text-2xl font-bold text-theme-primary">{formatTokens(tokenBalance.totalUsed)}</div>
            </div>

            <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
              <div className="text-sm text-theme-secondary mb-1">Tokens Bonus</div>
              <div className="text-2xl font-bold text-accent-violet">{formatTokens(tokenBalance.bonus)}</div>
            </div>
          </div>

          {/* Plan actuel */}
          <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
            <h3 className="text-lg font-bold text-theme-primary mb-4">Plan actuel</h3>
            {tokenBalance.currentPlan && tokenBalance.currentPlan !== 'free' ? (
              <div>
                <div className="text-2xl font-bold text-accent-primary mb-2 uppercase">
                  {tokenBalance.currentPlan}
                </div>
                <div className="text-theme-secondary text-sm">
                  Plan actif
                </div>
              </div>
            ) : (
              <div>
                <div className="text-theme-secondary text-sm mb-4">Plan Free</div>
                <button
                  onClick={onNavigate}
                  className="px-6 py-3 bg-accent-primary hover:bg-accent-secondary text-white font-semibold rounded-xl transition-colors"
                >
                  Upgrade vers Pro
                </button>
              </div>
            )}
          </div>
        </div>
      ) : subTab === 'manage' ? (
        <ManageSubscription onNavigateToBilling={onNavigate} />
      ) : (
        <div className="space-y-6">
          {/* Filtres Historique */}
          <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-theme-surface/50 border border-theme-border">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-theme-secondary" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-1.5 bg-theme-surface border border-theme-border rounded-lg text-theme-primary text-sm"
              >
                <option value="all">Tous les types</option>
                <option value="renewal">Renouvellements</option>
                <option value="usage">Consommation</option>
                <option value="purchase">Achat</option>
                <option value="bonus">Bonus</option>
                <option value="rollover">Rollover</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-theme-secondary" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-1.5 bg-theme-surface border border-theme-border rounded-lg text-theme-primary text-sm"
              >
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">7 derniers jours</option>
                <option value="month">30 derniers jours</option>
              </select>
            </div>
          </div>

          {/* Liste des transactions */}
          <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-theme-primary">Transactions récentes</h3>
              <span className="text-sm text-theme-secondary">{transactions.length} transaction(s)</span>
            </div>
            
            {loadingTransactions ? (
              <div className="text-center py-12 text-theme-secondary">Chargement...</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-theme-secondary">Aucune transaction</div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {transactions.map((t) => (
                  <div
                    key={t.id}
                    className="p-4 rounded-xl bg-theme-hover/50 border border-theme-border/50 hover:border-theme-border transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        {getTransactionIcon(t.transaction_type)}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-theme-primary truncate">
                            {t.description}
                          </div>
                          <div className="text-xs text-theme-secondary mt-1">
                            {new Date(t.created_at).toLocaleString('fr-FR')}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${getTransactionColor(t.transaction_type)}`}>
                          {t.tokens_amount > 0 ? '+' : ''}{formatTokens(t.tokens_amount)}
                        </div>
                        <div className="text-xs text-theme-tertiary mt-1">
                          Solde: {formatTokens(t.tokens_balance_after)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SECTION: FACTURATION
// ============================================

interface BillingSectionProps {
  user: any;
}

export function BillingSection({ user }: BillingSectionProps) {
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState(true);

  useEffect(() => {
    loadPayments();
    loadPaymentMethods();
  }, [user]);

  const loadPayments = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('stripe_payments')
        .select(`
          *,
          token_plans (name, slug)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      setPayments(data || []);
    } catch (error) {
      logger.error('Erreur chargement paiements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    if (!user) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = import.meta.env.VITE_API_URL || '${API_URL}';
      const response = await fetch(`${apiUrl}/api/billing/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.paymentMethods || []);
      }
    } catch (error) {
      logger.error('Erreur chargement moyens de paiement:', error);
    } finally {
      setLoadingMethods(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Méthodes de paiement */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Méthodes de paiement</h3>
        <p className="text-theme-secondary text-sm mb-4">
          Vos cartes enregistrées sont automatiquement sauvegardées lors de votre premier paiement
        </p>
        
        {loadingMethods ? (
          <div className="text-center py-4 text-theme-tertiary">Chargement...</div>
        ) : paymentMethods.length === 0 ? (
          <div className="p-4 bg-theme-hover/30 rounded-lg border border-theme-border/50 text-center">
            <p className="text-sm text-theme-secondary">
              Aucune carte enregistrée. Effectuez votre premier paiement pour sauvegarder votre carte.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 rounded-xl bg-theme-hover/50 border border-theme-border/50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-primary/20 to-accent-violet/20 border border-accent-primary/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-theme-primary font-semibold capitalize">
                      {method.brand} •••• {method.last4}
                    </div>
                    <div className="text-sm text-theme-secondary">
                      Expire {method.exp_month}/{method.exp_year}
                    </div>
                  </div>
                </div>
                {method.is_default && (
                  <div className="px-3 py-1 bg-accent-primary/20 border border-accent-primary/30 rounded-full text-xs text-accent-primary font-medium">
                    Par défaut
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historique des paiements */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Historique des paiements</h3>
        
        {loading ? (
          <div className="text-center py-8 text-theme-tertiary">Chargement...</div>
        ) : payments.length === 0 ? (
          <div className="text-center py-8 text-theme-tertiary">Aucun paiement pour le moment</div>
        ) : (
          <div className="space-y-3">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 rounded-xl bg-theme-hover/50 border border-theme-border/50"
              >
                <div>
                  <div className="text-theme-primary font-semibold">
                    {payment.token_plans?.name || 'Plan inconnu'}
                  </div>
                  <div className="text-sm text-theme-secondary">
                    {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-theme-primary font-bold">
                    {(payment.amount_cents / 100).toFixed(2)}€
                  </div>
                  <div className={`text-xs ${
                    payment.status === 'succeeded' ? 'text-green-400' :
                    payment.status === 'failed' ? 'text-red-400' :
                    'text-yellow-400'
                  }`}>
                    {payment.status === 'succeeded' ? 'Réussi' :
                     payment.status === 'failed' ? 'Échoué' :
                     'En attente'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// SECTION: PRÉFÉRENCES
// ============================================

interface PreferencesSectionProps {
  preferences: any;
  setPreferences: (prefs: any) => void;
  onSave: () => void;
}

export function PreferencesSection({ preferences, setPreferences, onSave }: PreferencesSectionProps) {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // TODO: Save to Supabase
    setSaving(true);
    setTimeout(() => {
      onSave();
      setSaving(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Notifications</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-theme-primary font-medium">Notifications par email</div>
              <div className="text-sm text-theme-secondary">Recevez des alertes par email</div>
            </div>
            <input
              type="checkbox"
              checked={preferences.email_notifications}
              onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })}
              className="w-5 h-5 rounded bg-theme-hover border-theme-border text-accent-primary focus:ring-accent-primary"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-theme-primary font-medium">Emails marketing</div>
              <div className="text-sm text-theme-secondary">Nouveautés, promotions et conseils</div>
            </div>
            <input
              type="checkbox"
              checked={preferences.marketing_emails}
              onChange={(e) => setPreferences({ ...preferences, marketing_emails: e.target.checked })}
              className="w-5 h-5 rounded bg-theme-hover border-theme-border text-accent-primary focus:ring-accent-primary"
            />
          </label>
        </div>
      </div>

      {/* Langue & Région */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-4">Langue & Région</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Langue</label>
            <select
              value={preferences.language}
              onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
              className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary focus:outline-none focus:border-accent-primary/50 transition-colors"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">Fuseau horaire</label>
            <select
              value={preferences.timezone}
              onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
              className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary focus:outline-none focus:border-accent-primary/50 transition-colors"
            >
              <option value="Europe/Paris">Paris (GMT+1)</option>
              <option value="America/New_York">New York (GMT-5)</option>
              <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:from-accent-secondary hover:to-accent-tertiary text-white font-semibold rounded-xl transition-all disabled:opacity-50"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les préférences'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// SECTION: DANGER ZONE
// ============================================

interface DangerZoneSectionProps {
  user: any;
  onSignOut: () => void;
  onNavigate: (path: string) => void;
}

export function DangerZoneSection({ user, onSignOut, onNavigate }: DangerZoneSectionProps) {
  const [confirmDelete, setConfirmDelete] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmDelete !== user?.email) {
      alert('Veuillez entrer votre email pour confirmer');
      return;
    }

    if (!confirm('Êtes-vous VRAIMENT sûr ? Cette action est irréversible !')) {
      return;
    }

    setDeleting(true);
    try {
      // TODO: Appeler l'API pour supprimer le compte
      const { error } = await supabase.rpc('delete_user_account', { user_id: user.id });
      
      if (error) throw error;
      
      await onSignOut();
      onNavigate('/');
    } catch (error) {
      logger.error('Erreur suppression compte:', error);
      alert('Erreur lors de la suppression du compte');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Avertissement */}
      <div className="p-6 rounded-2xl bg-theme-error/10 border border-theme-error/30">
        <h3 className="text-lg font-bold text-theme-error mb-2">Actions Sensibles</h3>
        <p className="text-theme-secondary text-sm">
          Les actions ci-dessous sont irréversibles. Assurez-vous de bien comprendre les conséquences avant de continuer.
        </p>
      </div>

      {/* Déconnexion */}
      <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-bold text-theme-primary mb-2">Déconnexion</h3>
        <p className="text-theme-secondary text-sm mb-4">
          Vous serez déconnecté de votre compte sur cet appareil
        </p>
        <button
          onClick={onSignOut}
          className="px-6 py-3 bg-theme-hover hover:bg-theme-elevated text-theme-primary font-semibold rounded-xl transition-colors"
        >
          Se déconnecter
        </button>
      </div>

      {/* Suppression du compte */}
      <div className="p-6 rounded-2xl bg-theme-error/10 border border-theme-error/30">
        <h3 className="text-lg font-bold text-red-400 mb-2">Supprimer le compte</h3>
        <p className="text-theme-secondary text-sm mb-4">
          Une fois votre compte supprimé, toutes vos données seront définitivement effacées. Cette action est irréversible.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-2">
              Entrez votre email pour confirmer : <span className="text-theme-primary font-mono">{user?.email}</span>
            </label>
            <input
              type="email"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              className="w-full px-4 py-3 bg-theme-surface border border-theme-error/30 rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-theme-error/50 transition-colors"
              placeholder="votre@email.com"
            />
          </div>

          <button
            onClick={handleDeleteAccount}
            disabled={deleting || confirmDelete !== user?.email}
            className="px-6 py-3 bg-theme-error hover:bg-theme-error/80 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? 'Suppression...' : 'Supprimer définitivement mon compte'}
          </button>
        </div>
      </div>
    </div>
  );
}

